import FlowLayout from "components/layout/FlowLayout";
import "./RequestSystemAccess.scoped.scss";
import { Routes } from "components/routing/Routing";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getUserDetails,
  requestSystemAccess, setCreateNewRequest, setRoleAssignmentUsers, setSystemAccessRequestReason, setIsRoleAssignmentClicked,
  setRequestsWereUpdated
} from "store/userManagement/userManagementActions";
import useSelector from "store/useSelector";
import "../management/UserDetail.scoped.scss";
import ModalSpinner from "components/common/ModalSpinner";
import { AuthFilter } from "auth/AuthFilter";
import { Roles } from "interfaces/user/UserInterfaces";
import RoleAssignmentTable from "../RoleAssignmentTable";
import { AddRoleAssignmentService } from "../../../services/addRoleAssignmentService";
import SystemAccessRequestStatus from "../SystemAccessRequestStatus";
import { IMySystemAccessRequestResponse } from "../../../interfaces/user/UserInterfaces";
import SystemAccessRequestApi from "../../../apis/systemAccessRequests/SystemAccessRequestApi";

const RequestSystemAccess: React.FC = (props) => {
  const location = useLocation();
  const {
    auth: {
      currentUser: {
        email,
        name,
        roles
      }
    },
    userManagement: {
      bulkEditUserData: {
        selectedUsers,
        roleAssignments,
        saveOperation,
        systemAccessRequestReason,
        systemAccessRequestId,
        createNewRequest,
        addRoleAssignmentClicked,
        requestsWereUpdated
      }
    }
  } = useSelector(store => store);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const requestReason = systemAccessRequestReason ?? "";
  const setRequestReason = (value: string) => dispatch(setSystemAccessRequestReason(value));
  const [reasonTouched, setReasonTouched] = useState(false);
  const [existingRequest, setExistingRequest] = useState<IMySystemAccessRequestResponse[] | null>(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const hasRoleAssignments = (roleAssignments?.length ?? 0) > 0;
  const isReasonValid = requestReason.trim().length > 0;
  const canSubmit = isReasonValid && hasRoleAssignments;
  const roleAssignmentService =
    new AddRoleAssignmentService(Routes.SystemAccessEditRoleAssignment, dispatch, navigate);
    let roleAssignmentsDetail: React.ReactElement | undefined;

  useEffect(() => {
    dispatch(setRoleAssignmentUsers([{
      email: email,
      name: name
    }]))
    dispatch(getUserDetails({ email: email }));
  }, [dispatch, email, name]);

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const data = await SystemAccessRequestApi.getMySystemAccessRequest();
        setExistingRequest(data);
      } catch {
        // Silently fail
      } finally {
        setCheckingExisting(false);
      }
    };
    checkExisting();
    if(addRoleAssignmentClicked) {
      dispatch(getUserDetails({ email: email }));
      roleAssignmentService.onAddRoleAssignment();
      dispatch(setIsRoleAssignmentClicked(false));
    }
  }, []);

  // Re-check when the current system access request id in store changes
  useEffect(() => {
    if (!requestsWereUpdated) return;
    const refresh = async () => {
      try {
        setCheckingExisting(true);
        const data = await SystemAccessRequestApi.getMySystemAccessRequest();
        setExistingRequest(data);
        dispatch(setRequestsWereUpdated(true));
      } catch {
        // Silently fail
      } finally {
        setCheckingExisting(false);
      }
    };
    refresh();
  }, [requestsWereUpdated]);

  if (checkingExisting) {
    return (
      <FlowLayout header={<h2 className="page-title">{`Request${roles.filter(r => r !== Roles.ReportViewer).length > 0 ? " Additional " : " "}Access`}</h2>}>
        <ModalSpinner />
      </FlowLayout>
    );
  }

  if (existingRequest?.some(r => r.exists) && !showForm && !hasRoleAssignments && !createNewRequest) {
    return (
      <FlowLayout 
        header={<h2 className="page-title">{`Request${roles.filter(r => r !== Roles.ReportViewer).length > 0 ? " Additional " : " "}Access`}</h2>} 
        footer={<button className="primary-button save-button" onClick={() => {
          setShowForm(true)
          dispatch(setCreateNewRequest(true))
          roleAssignmentService.onRemoveRoleAssignments();
          setRequestReason("");
          }}>Create New Request</button>}>
        <SystemAccessRequestStatus onSubmitNew={() => setShowForm(true)} />
      </FlowLayout>
    );
  }

  
  if (selectedUsers
    && roleAssignments
    && roleAssignments.length > 0) {
    roleAssignmentsDetail = <RoleAssignmentTable roleAssignments={roleAssignments} roleAssignmentService={roleAssignmentService} />
  }

  return (
    <FlowLayout
      header={(
        <div>
          <div className="toolbar">
            <h2 className="page-title">
              <span>{`Request${roles.filter(r => r !== Roles.ReportViewer).length > 0 ? " Additional " : " "}Access`}</span>
            </h2>
            <div className="buttons">
              <button
                className="primary-button save-button"
                disabled={!canSubmit}
                onClick={() => {
                  setReasonTouched(true);
                  if (!canSubmit) return;
                  dispatch(requestSystemAccess(
                    { roleAssignments: roleAssignments ?? [], requestReason: requestReason.trim(), navigate: navigate }));
                    dispatch(setCreateNewRequest(false))
                    dispatch(setIsRoleAssignmentClicked(false))
                }}
              >
                Submit Request
              </button>
            </div>
          </div>
          <p className="instructions">Click "Add Role Assignment" to include access permissions to your request. Click 'Submit Request' when completed.</p>
          <div className="reason-field">
            <label htmlFor="requestReason">Reason for Access Request <span className="required">*</span></label>
            <textarea
              id="requestReason"
              className={reasonTouched && !requestReason.trim() ? "error" : ""}
              placeholder="Please provide a reason for your access request..."
              value={requestReason}
              maxLength={2000}
              rows={3}
              onChange={(e) => setRequestReason(e.target.value)}
              data-testid="requestReason"
              onBlur={() => setReasonTouched(true)}
            />
            {reasonTouched && !isReasonValid && (
              <span className="error-message">Reason for access request is required.</span>
            )}
            {!hasRoleAssignments && !saveOperation?.isWorking && (
              <span className="error-message">Please add at least one role assignment before submitting.</span>
            )}
          </div>
        </div>
      )}
      footer={
        <div className="footer-wrapper">
          <div className="add-role-assignment-button-wrapper">
            <div
              className="primary-button add-role-assignment-button"
              onClick={() => {
                 dispatch(setIsRoleAssignmentClicked(true));
                 roleAssignmentService.onAddRoleAssignment() 
                }}
            >
              Add Role Assignment
            </div>
          </div>
        </div>
      }
    >
      {saveOperation?.isWorking &&
        <ModalSpinner />
      }
      {roleAssignmentsDetail}
    </FlowLayout>
  );

}

export default RequestSystemAccess;